import { Injectable, NgZone } from '@angular/core';
import { ToasterService } from '@lib/components/toaster/toaster.service';
import { storage } from '@lib/utils/storage/storage.utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs/operators';
import { clearAppSession, hydrateAppSession, setAppSession } from './app-session.actions';

@Injectable()
export class AppSessionEffects {
    private isProcessingExternalEvent = false;
    private channel?: BroadcastChannel;
    private readonly tabId = Math.random().toString(36).slice(2, 11); //NOSONAR
    private lastProcessedEvent = '';

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly ngZone: NgZone,
        private readonly toaster: ToasterService,
    ) {
        this.initializeCrossTabSync();
    }

    persistSession$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(setAppSession),
                tap(({ session }) => {
                    if (session && !this.isProcessingExternalEvent) {
                        try {
                            storage.setItem('appSession', session);
                            this.broadcastSessionUpdate(session);
                        } catch (error) {
                            this.toaster.show({
                                message:
                                    'Session sync issue detected. Please refresh if you experience login problems.',
                                status: 'warning',
                            });
                            console.warn('Failed to persist session:', error);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    clearSession$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(clearAppSession),
                tap(() => {
                    if (!this.isProcessingExternalEvent) {
                        try {
                            storage.removeItem('appSession');
                            this.broadcastLogout();
                        } catch (error) {
                            console.warn('Failed to clear session:', error);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    private initializeCrossTabSync(): void {
        try {
            if ('BroadcastChannel' in globalThis) {
                this.channel = new BroadcastChannel('auth-sync');
                this.channel.addEventListener('message', (event) => {
                    this.handleBroadcastMessage(event);
                });
            }

            globalThis.addEventListener('storage', (event) => {
                this.handleStorageEvent(event);
            });

            globalThis.addEventListener('beforeunload', () => {
                this.cleanup();
            });
        } catch (error) {
            console.warn('Failed to initialize cross-tab sync:', error);
        }
    }

    private handleBroadcastMessage(event: MessageEvent): void {
        try {
            const { type, session, tabId, eventId } = event.data;

            if (tabId === this.tabId || this.lastProcessedEvent === eventId) {
                return;
            }

            this.lastProcessedEvent = eventId;

            this.ngZone.run(() => {
                this.isProcessingExternalEvent = true;

                setTimeout(() => {
                    if (type === 'SESSION_UPDATE' && session) {
                        this.store.dispatch(hydrateAppSession({ session }));
                    } else if (type === 'LOGOUT') {
                        this.store.dispatch(clearAppSession());
                    }

                    this.isProcessingExternalEvent = false;
                }, 0);
            });
        } catch (error) {
            console.warn('Failed to handle broadcast message:', error);
            this.isProcessingExternalEvent = false;
        }
    }

    private handleStorageEvent(event: StorageEvent): void {
        if (this.channel || event.key !== 'appSession') {
            return;
        }

        try {
            this.ngZone.run(() => {
                this.isProcessingExternalEvent = true;

                setTimeout(() => {
                    if (event.newValue) {
                        const session = JSON.parse(event.newValue);
                        this.store.dispatch(hydrateAppSession({ session }));
                    } else if (event.oldValue && !event.newValue) {
                        this.store.dispatch(clearAppSession());
                    }

                    this.isProcessingExternalEvent = false;
                }, 0);
            });
        } catch (error) {
            console.warn('Failed to handle storage event:', error);
            this.isProcessingExternalEvent = false;
        }
    }

    private broadcastSessionUpdate(session: any): void {
        if (!this.channel) return;

        try {
            const eventId = `session_${Date.now()}_${this.tabId}`;
            this.channel.postMessage({
                type: 'SESSION_UPDATE',
                session,
                tabId: this.tabId,
                eventId,
            });
        } catch (error) {
            console.warn('Failed to broadcast session update:', error);
        }
    }

    private broadcastLogout(): void {
        if (!this.channel) return;

        try {
            const eventId = `logout_${Date.now()}_${this.tabId}`;
            this.channel.postMessage({
                type: 'LOGOUT',
                tabId: this.tabId,
                eventId,
            });
        } catch (error) {
            console.warn('Failed to broadcast logout:', error);
        }
    }

    private cleanup(): void {
        try {
            if (this.channel) {
                this.channel.close();
            }
        } catch (error) {
            console.warn('Failed to cleanup cross-tab sync:', error);
        }
    }
}
